import { Plus, Trash2 } from 'lucide-react'
import { useMatch } from 'react-router'
import { H4 } from '@/components/sv/typographies'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { AddExceptionSheet } from '@/routes/single-sign-on/exceptions/add-exception-sheet'
import { RemoveExceptionDialog } from '@/routes/single-sign-on/exceptions/remove-exception-dialog'
import type {
  ExceptionsSsoQuery,
  ExceptionsSsoQueryVariables,
} from '@/routes/single-sign-on/exceptions/index.types.gen'

const EXCEPTIONS_SSO_QUERY = gql`
  query ExceptionsSso($idCompany: String!) {
    company(id: $idCompany) {
      id
      users {
        id
        capabilities @live {
          id
          isSamlExempt
        }
        user {
          id
          email
          firstName
          lastName
        }
      }
    }
  }
`

export const Exceptions = () => {
  const { idCompany } = useIdCompany()
  const { navigateToPath } = useCompanyPath()
  const isAddSheetOpen = Boolean(useMatch('/single-sign-on/add-exception'))
  const isRemoveDialogOpen = Boolean(
    useMatch('/single-sign-on/remove-exception/:idUser')
  )

  const { data } = useSuspenseQuery<
    ExceptionsSsoQuery,
    ExceptionsSsoQueryVariables
  >(EXCEPTIONS_SSO_QUERY, {
    variables: { idCompany },
  })

  const users = data?.company?.users ?? []

  const exceptionUsers = users
    .filter((cu) => cu.capabilities?.some((c) => c.isSamlExempt))
    .map((cu) => ({
      id: cu.id,
      idUser: cu.user?.id ?? '',
      email: cu.user?.email ?? '',
      firstName: cu.user?.firstName ?? '',
      lastName: cu.user?.lastName ?? '',
    }))

  const hasNonExceptionUsers = users.some(
    (cu) => !cu.capabilities?.some((c) => c.isSamlExempt)
  )

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            <H4>Exceptions</H4>
          </CardTitle>
          <CardAction>
            <Button
              variant="outline"
              onClick={() =>
                navigateToPath({
                  pathname: '/single-sign-on/add-exception',
                  options: { replace: true },
                })
              }
              disabled={!hasNonExceptionUsers}
            >
              <Plus aria-hidden="true" />
              Add exception
            </Button>
          </CardAction>
          <CardDescription>
            Users on the exceptions list can sign in with a password even when
            SSO is mandatory.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {exceptionUsers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No users have been added as exceptions.
            </p>
          ) : (
            <div className="space-y-3">
              {exceptionUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">
                        {getInitials(user.firstName, user.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove SSO exception for ${user.firstName} ${user.lastName}`}
                    onClick={() =>
                      navigateToPath({
                        pathname: '/single-sign-on/remove-exception/:idUser',
                        variables: { idUser: user.idUser },
                        options: { replace: true },
                      })
                    }
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddExceptionSheet
        isOpen={isAddSheetOpen}
        onClose={() =>
          navigateToPath({
            pathname: '/single-sign-on',
            options: { replace: true },
          })
        }
      />

      <RemoveExceptionDialog
        isOpen={isRemoveDialogOpen}
        onClose={() =>
          navigateToPath({
            pathname: '/single-sign-on',
            options: { replace: true },
          })
        }
      />
    </>
  )
}
